import { useEffect, useRef } from 'react';

export default function CandlestickAnimation() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    interface Candle {
      x: number;
      y: number;
      open: number;
      close: number;
      high: number;
      low: number;
      width: number;
      opacity: number;
      speed: number;
      color: string;
    }

    const candles: Candle[] = [];
    const candleCount = 15;

    for (let i = 0; i < candleCount; i++) {
      const isGreen = Math.random() > 0.5;
      const open = Math.random() * 40 + 20;
      const close = isGreen ? open + Math.random() * 30 : open - Math.random() * 30;
      
      candles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        open,
        close,
        high: Math.max(open, close) + Math.random() * 15,
        low: Math.min(open, close) - Math.random() * 15,
        width: 8,
        opacity: Math.random() * 0.3 + 0.1,
        speed: Math.random() * 0.3 + 0.1,
        color: isGreen ? '#10b981' : '#ef4444'
      });
    }

    const drawCandle = (candle: Candle) => {
      ctx.globalAlpha = candle.opacity;
      
      // Draw wick
      ctx.strokeStyle = candle.color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(candle.x + candle.width / 2, candle.y - candle.high);
      ctx.lineTo(candle.x + candle.width / 2, candle.y - candle.low);
      ctx.stroke();

      // Draw body
      ctx.fillStyle = candle.color;
      const bodyHeight = Math.abs(candle.close - candle.open);
      const bodyY = candle.y - Math.max(candle.open, candle.close);
      ctx.fillRect(candle.x, bodyY, candle.width, bodyHeight);
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      candles.forEach((candle) => {
        drawCandle(candle);
        
        candle.y += candle.speed;
        
        if (candle.y > canvas.height + 100) {
          candle.y = -100;
          candle.x = Math.random() * canvas.width;
        }
      });

      requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full pointer-events-none z-0"
      style={{ opacity: 0.4 }}
    />
  );
}